/**
 * 레이지 무브 발사 속도 계산(순수). RageMoves(물리 배선)와 단위 테스트가 공유한다.
 * 불변식은 tests/unit/rage.test.ts가 카탈로그/레이아웃 값과 교차 검증한다:
 *  - sweep 배팅 속도는 팔 속도에 비례 — 느린 팔은 최저 임계값 미만(안 부서짐), 빠른 팔은
 *    최고 임계값 초과(벽/바닥 충돌로 전부 깨질 수 있다).
 *  - flip 상향 속도 → 천장 도달 시 잔여 속도 ≥ 천장 즉시 파괴 최소 속도.
 */
import type { Vec3 } from '../core/types';
import { BALANCE, type BalanceConfig } from '../config/balance';

export type SweepDir = 1 | -1;
type RageConfig = BalanceConfig['rage'];

/**
 * 스윕 제스처 시작 지점 → 쓸기 방향. 책상 왼쪽에서 시작하면 오른팔을 왼쪽으로 차징해
 * 좌→우로 쓸고(+1), 오른쪽에서 시작하면 왼팔을 오른쪽으로 차징해 우→좌로 쓴다(-1).
 */
export function sweepDirForStart(worldX: number): SweepDir {
  return worldX < 0 ? 1 : -1;
}

/**
 * 쓸어버리는 팔이 지나가며 물건을 쳐낼 때 전달하는 속도. 팔의 실제 이동속도(armSpeed, m/s)에
 * 비례한다 — 살살 쓸면(armSpeed 작음) 살짝 밀리기만 하고, 세게 쓸면 강하게 튕겨나가 벽/바닥
 * 충돌로 깨진다(파괴는 기존 충돌 경로가 담당). 리프트도 속도 비례라 살살 쓸면 거의 뜨지 않는다.
 */
export function sweepBatVelocity(
  dir: SweepDir,
  armSpeed: number,
  rand: () => number,
  cfg: RageConfig = BALANCE.rage,
): Vec3 {
  const speed = armSpeed * cfg.sweep.transfer;
  return {
    x: dir * speed,
    y: Math.min(cfg.sweep.maxLift, speed * cfg.sweep.liftRatio),
    z: (rand() - 0.5) * 2 * cfg.sweep.lateral,
  };
}

/** 책상 뒤집기: 물건을 위로 쏘아올림(천장 파괴) + 좌우 산포 + 뒤로 살짝(카메라 반대쪽). */
export function flipLaunchVelocity(rand: () => number, cfg: RageConfig = BALANCE.rage): Vec3 {
  return {
    x: (rand() - 0.5) * 2 * cfg.flip.lateral,
    y: cfg.flip.up + rand() * cfg.flip.jitter,
    z: -(0.3 + rand() * cfg.flip.back),
  };
}

/** 발사 중 무작위 텀블링 각속도(각 축 ±spin/2). */
export function tumbleSpin(spin: number, rand: () => number): Vec3 {
  return {
    x: (rand() - 0.5) * spin,
    y: (rand() - 0.5) * spin,
    z: (rand() - 0.5) * spin,
  };
}
